import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as actions from '~/store/actions';
import * as actionTypes from '~/store/actions/actionTypes';
import { useControlSessions } from '~/hooks';
import { log } from '~/helpers';
import * as lib from '~/dependencies';
import messagesToast from '~/validations/messagesNotifications';

const MAX_ATTEMPTS = 10;

export default function useTryToCall() {
  // HOOKS
  const controlSessions = useControlSessions();
  const dispatch = useDispatch();

  // SELECTORS
  const statusSystem = useSelector(state => state.system?.status);
  /** The country code extracted from the current user callerId */
  const countryCode = useSelector(state => state.system?.callerIdCountryCode);
  const sessionActive = useSelector(state => state.callCurrent?.session);
  const ua = useSelector(state => state.phone?.ua);

  // MEMO's
  const statusSystemMemo = useMemo(() => statusSystem, [statusSystem]);
  const countryCodeMemo = useMemo(() => countryCode, [countryCode]);

  // Resolve a promise if the UA is registered.
  const tryToRegister = useCallback(() => {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const interval = setInterval(() => {
        attempts += 1;

        if (attempts > MAX_ATTEMPTS) {
          reject();
          clearInterval(interval);
        } else if (ua?.isRegistered()) {
          resolve();
          clearInterval(interval);
        }
      }, 1100);
    });
  }, [ua]);

  const tryToCall = useCallback(
    async (number, extra) => {
      try {
        if (typeof number !== 'string' && typeof number !== 'number')
          throw new Error('Invalid phone number.', {
            cause: 'Phone number is NOT typeof string or number',
          });

        let phoneNumber = String(number);

        // Countries which onforce zero after country code for international number e.g: 39 - Italy
        const enforceZeroPrefixExceptions = ['39'];

        if (phoneNumber.startsWith(`00${countryCodeMemo}0`)) {
          if (!enforceZeroPrefixExceptions.includes(countryCodeMemo)) {
            phoneNumber = phoneNumber
              .split(`00${countryCodeMemo}0`)
              .join(`00${countryCodeMemo}`);
          }
        }

        const name = extra?.name || null;
        const contactId = extra?.contactId || -1;

        if (statusSystemMemo === actionTypes.STATUS.BUSY) {
          await ua.register();
          await tryToRegister();
        }

        // Hold active call before make a new start new calls
        if (sessionActive) controlSessions(sessionActive.id, 'hold');

        dispatch(actions.setLastDialedNumber(phoneNumber));
        dispatch(actions.numberToTransfer(phoneNumber));

        controlSessions(null, 'invite', phoneNumber, {
          name,
          contactId,
        });
      } catch (err) {
        log.error('Failed to execute the tryToCall function.', err);
        lib.ToastMessage(
          'warn',
          messagesToast().NOTIFICATIONS.register.failed,
          6000
        );
      }
    },
    [
      countryCodeMemo,
      statusSystemMemo,
      ua,
      tryToRegister,
      dispatch,
      sessionActive,
      controlSessions,
    ]
  );

  return tryToCall;
}
